import {
  Box,
  Button,
  Card,
  Grid,
  markedForDeletionStyle,
  MergeInfoBlock,
  pn,
  Stack,
  Typography,
  useElementBreakpoints,
  useTheme,
  ValidationErrors,
} from "@compass/core";
import { Select } from "components";
import { ContributorBulkMergeEmployment, ContributorBulkMergeResource } from "generated/openApi";
import produce, { original } from "immer";
import { useEffect } from "react";
import { BasicInfo } from "./BasicInfo";

export const ContributorEmployments = (props: {
  employments: ContributorBulkMergeEmployment[];
  setEmployments: (employments: ContributorBulkMergeEmployment[]) => void;
  resource: ContributorBulkMergeResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const properties = props.resource.employmentProperties || [];
  const theme = useTheme();
  const keepingEmployments = props.employments.filter((x) => x.keeping);

  useEffect(() => {
    clearDeletingAssignMatchesToEmploymentIds();
  }, [props.employments.map((x) => x.keeping)]);

  const clearDeletingAssignMatchesToEmploymentIds = () => {
    props.setEmployments(
      produce(props.employments, (draft) => {
        draft?.forEach((x) => {
          if (
            x.assignMatchesToEmploymentId &&
            !keepingEmployments?.map((x) => x.id)?.includes(x.assignMatchesToEmploymentId)
          )
            x.assignMatchesToEmploymentId = undefined;
        });
      })
    );
  };

  return (
    <Stack ref={breakpointsRef} direction="column" spacing="md">
      <Typography variant="caption">
        Select a contributor to keep. The rest of the contributors will be deleted and their data will be transferred to
        the keeping contributor.
      </Typography>

      {props.employments.map((employment, index) => {
        const _validationErrors = props.validationErrors[index];

        const markedForDelete = !keepingEmployments?.includes(employment);
        const removingSx = markedForDelete ? markedForDeletionStyle(theme) : undefined;

        return (
          <Card
            header={
              <Grid container direction="row" spacing="md" alignItems="center">
                <Grid item>
                  <Button
                    variant={employment.keeping ? "contained" : "outlined"}
                    onClick={() =>
                      props.setEmployments(
                        produce(props.employments, (draft) => {
                          const _employment = draft.find((x) => original(x) === employment);
                          _employment!.keeping = !_employment?.keeping;
                        })
                      )
                    }
                  >
                    {employment.keeping ? "Keeping" : "Keep"}
                  </Button>
                </Grid>

                <Grid item>
                  <Typography variant="inherit">Employment {employment.number}</Typography>
                </Grid>

                {!employment.keeping && employment.usedByContributorMatches > 0 && (
                  <Grid item flexGrow={1}>
                    <Select
                      label={
                        properties.find(
                          (x) => x.propertyName === pn<ContributorBulkMergeEmployment>("assignMatchesToEmploymentId")
                        )?.displayName
                      }
                      errors={_validationErrors?.[pn<ContributorBulkMergeEmployment>("assignMatchesToEmploymentId")]}
                      required
                      value={keepingEmployments?.find((x) => x.id === employment.assignMatchesToEmploymentId)}
                      options={keepingEmployments}
                      getOptionLabel={(option) => "Employment " + option.number}
                      size="small"
                      onChange={(e, value) =>
                        props.setEmployments(
                          produce(props.employments, (draft) => {
                            const _employment = draft.find((x) => original(x) === employment);
                            _employment!.assignMatchesToEmploymentId = value?.id;
                          })
                        )
                      }
                    />
                  </Grid>
                )}
              </Grid>
            }
          >
            <Grid container spacing="lg" sx={{ ...removingSx }}>
              <Grid item xs={setBreakpoints({ md: 8, xs: 12 })}>
                <BasicInfo employment={employment} resource={props.resource} />
              </Grid>

              <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
                <Box display="flex" alignItems={"center"} justifyContent="center" height="100%">
                  <MergeInfoBlock
                    value={employment.usedByContributorMatches}
                    label={
                      properties.find(
                        (x) => x.propertyName === pn<ContributorBulkMergeEmployment>("usedByContributorMatches")
                      )?.displayName
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Card>
        );
      })}
    </Stack>
  );
};
